import { FunctionComponent } from 'react';
import EvaldziaLoginImage from './assets/e-valdzia-login.svg';
import VilniusLoginImage from './assets/vilnius-login.png';
import { useId } from 'react-id-generator';
import { ReactComponent as ArrowRightIcon } from 'assets/icons/Chevron right.svg';
import { ReactComponent as CloseIcon } from 'assets/icons/Close.svg';
import IconButton from 'components/icon-button';
import classNames from 'classnames';
import { LOGIN_EVARTAI_LOGIN, LOGIN_VILNIUS_LOGIN } from '../../constants/api';
import { loginForm } from '../../utils/login-form';
import { useNotifications } from '../../providers/notifications';
import { ROUTES } from '../../constants/routes';
import { generatePath } from 'react-router-dom';
import Button from '../../components/button';
import { useCity } from '../../hooks/useCity';
import { useTranslation } from 'react-i18next';
import { getCityIdFromUrl, withCityId } from 'utils/city-url';

interface SigninPageProps {
  open: boolean;
  onClose: () => void;
}

const SigninPage: FunctionComponent<SigninPageProps> = ({ open, onClose }) => {
  const { notify } = useNotifications();
  const { canRegisterAnonymously } = useCity();
  const { t } = useTranslation();
  const cityId = getCityIdFromUrl();
  const cityAwareHomeRoute = withCityId(ROUTES.home, cityId) as string;
  const cityAwareNewMessageRoute = withCityId(
    generatePath(ROUTES.newMessage, { '*': '0' }),
    cityId,
  ) as string;

  const signIn = (loginMethod: string) => {
    const form = loginForm(loginMethod);
    if (form) {
      document.body.appendChild(form);
      form.submit();
      document.body.removeChild(form);
    } else {
      notify({ text: t('login.errorOccurred'), type: 'error' });
      setTimeout(() => {
        window.location.replace(cityAwareHomeRoute);
      }, 3000);
    }
  };

  const logins = [
    {
      image: VilniusLoginImage,
      title: t('login.vilniusLogin'),
      handle: () => signIn(LOGIN_VILNIUS_LOGIN),
    },
    {
      image: EvaldziaLoginImage,
      title: t('login.eGovernmentGate'),
      handle: () => signIn(LOGIN_EVARTAI_LOGIN),
    },
  ];

  const ids = useId(logins.length);

  const shouldDisplay = open;

  return (
    <div
      className={classNames(
        'fixed inset-0 z-[1000] flex overflow-hidden transition duration-300',
        {
          'invisible pointer-events-none bg-black/0': !shouldDisplay,
          'bg-black/70': shouldDisplay,
        },
      )}
    >
      <div
        className={classNames(
          'max-w-[580px] w-full sm:rounded-[16px] sm:px-7 py-9 bg-white sm:m-auto mt-auto relative transform duration-300 transition',
          {
            'translate-y-full sm:translate-y-7 sm:opacity-0': !shouldDisplay,
            'transalte-y-0 sm:opacity-100': shouldDisplay,
          },
        )}
      >
        <IconButton onClick={onClose} className='absolute top-4 right-4'>
          <CloseIcon />
        </IconButton>
        <div className='mb-3 text-heading-sm font-bold text-center font-ubuntu'>
          {t('login.login')}
        </div>
        <div className='mb-7 text-center'>{t('login.choose')}</div>
        <div className='max-w-[420px] w-full mx-auto'>
          {logins.map((login, i) => (
            <div
              onClick={login.handle}
              key={ids[i]}
              className='cursor-pointer select-none sm:rounded-lg hover:bg-black/5 active:bg-black/10 transition px-5'
            >
              <div
                className={classNames(
                  'flex items-center h-[94px] justify-between',
                  {
                    'border-b border-theme-border': i !== logins.length - 1,
                  },
                )}
              >
                <div className='flex items-center'>
                  <img className='w-14 h-14 mr-4' src={login.image} alt='' />
                  <div className='font-bold'>{login.title}</div>
                </div>
                <ArrowRightIcon />
              </div>
            </div>
          ))}
        </div>
        {canRegisterAnonymously && (
          <div className='justify-center flex mt-4'>
            <Button
              onClick={() => window.location.replace(cityAwareNewMessageRoute)}
            >
              {t('login.anonymouse')}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default SigninPage;
